import Box from "@mui/material/Box";
import { alpha, type SxProps, type Theme } from "@mui/material/styles";

export interface StatusChipProps {
  label: string;
  color: string;
  halo?: boolean;
  pulse?: boolean;
  sx?: SxProps<Theme>;
  dataTestId?: string;
}

export function StatusChip({
  label,
  color,
  halo = true,
  pulse = false,
  sx,
  dataTestId,
}: StatusChipProps) {
  const haloShadow = halo ? `0 0 0 3px ${alpha(color, 0.25)}` : "none";
  const shouldAnimate = halo && pulse;

  return (
    <Box
      sx={[
        {
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          color: "text.primary",
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box
        aria-hidden
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          backgroundColor: color,
          flex: "none",
          boxShadow: haloShadow,
          ...(shouldAnimate && {
            animation: "status-chip-pulse 1.8s ease-in-out infinite",
            "@keyframes status-chip-pulse": {
              "0%, 100%": { boxShadow: `0 0 0 3px ${alpha(color, 0.25)}` },
              "50%": { boxShadow: `0 0 0 6px ${alpha(color, 0.12)}` },
            },
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
            },
          }),
        }}
      />
      <Box component="span" data-testid={dataTestId}>
        {label}
      </Box>
    </Box>
  );
}
